import { CalendarDate, PlacementId, TimeEntryId } from "@sp-crm/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { TimeEntryEditForm } from "components/clients/show-client/time-entry-edit-form";
import {
    formatDuration,
    formatDurationFriendly,
} from "components/clients/show-client/time-entry-utils";
import { trpc, trpcClient } from "components/rpc/trpc";
import { DeleteButton, EditButton } from "components/ui/action-button";
import { fancyAlert } from "components/ui/fancy-alert";
import { Spinner } from "components/ui/spinner";
import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";

interface Props {
    clientId: PlacementId;
    clientName: string;
    onNavigate?: () => void;
}

const PREVIEW_LIMIT = 5;

interface TrayEntry {
    id: TimeEntryId;
    startTime: string;
    durationMinutes: number;
    note?: string | null;
}

export const TrayTimeEntriesSummary: React.FC<Props> = ({
    clientId,
    clientName,
    onNavigate,
}) => {
    const trpcHooks = trpc.useTRPC();
    const queryClient = useQueryClient();
    const [showAll, setShowAll] = useState(false);
    const { data: entries, isLoading } = useQuery(
        trpcHooks.timeEntries.listByClient.queryOptions({ clientId, limit: 200 }),
    );

    const invalidate = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: [["timeEntries"]] });
    }, [queryClient]);

    if (isLoading) {
        return (
            <p className="text-xs text-zinc-500 dark:text-zinc-400">Loading activity…</p>
        );
    }

    const allEntries = entries ?? [];
    const totalMinutes = allEntries.reduce((sum, e) => sum + e.durationMinutes, 0);
    const visible = showAll ? allEntries : allEntries.slice(0, PREVIEW_LIMIT);
    const hiddenCount = allEntries.length - visible.length;

    if (allEntries.length === 0) {
        return (
            <div className="space-y-1 border-t border-zinc-100 pt-3 dark:border-zinc-800">
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                    No time tracked for {clientName} yet.
                </p>
                <ReportLink onNavigate={onNavigate} />
            </div>
        );
    }

    return (
        <div className="space-y-2 border-t border-zinc-100 pt-3 dark:border-zinc-800">
            <p className="text-xs text-zinc-600 dark:text-zinc-300">
                You&rsquo;ve tracked{" "}
                <span className="font-semibold text-brand-700 dark:text-brand-200">
                    {formatDurationFriendly(totalMinutes)}
                </span>{" "}
                for {clientName} across {allEntries.length}{" "}
                {allEntries.length === 1 ? "entry" : "entries"}.
            </p>
            <ul className="space-y-1">
                {visible.map(entry => (
                    <TrayTimeEntryRow
                        key={entry.id}
                        entry={entry}
                        onChanged={invalidate}
                    />
                ))}
            </ul>
            {hiddenCount > 0 && (
                <button
                    type="button"
                    onClick={() => setShowAll(true)}
                    className="block text-xs text-zinc-500 underline underline-offset-2 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200">
                    Show {hiddenCount} more
                </button>
            )}
            {showAll && allEntries.length > PREVIEW_LIMIT && (
                <button
                    type="button"
                    onClick={() => setShowAll(false)}
                    className="block text-xs text-zinc-500 underline underline-offset-2 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200">
                    Show fewer
                </button>
            )}
            <div className="pt-2">
                <ReportLink onNavigate={onNavigate} />
            </div>
        </div>
    );
};

interface TrayTimeEntryRowProps {
    entry: TrayEntry;
    onChanged: () => void;
}

const TrayTimeEntryRow: React.FC<TrayTimeEntryRowProps> = ({ entry, onChanged }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleSaved = useCallback(() => {
        setIsEditing(false);
        onChanged();
    }, [onChanged]);

    const handleDelete = useCallback(async () => {
        setIsDeleting(true);
        try {
            await trpcClient.timeEntries.delete.mutate({ id: entry.id });
            onChanged();
        } catch {
            await fancyAlert("Error", "Failed to delete time entry. Please try again.");
        } finally {
            setIsDeleting(false);
        }
    }, [entry.id, onChanged]);

    if (isEditing) {
        return (
            <li>
                <TimeEntryEditForm
                    entryId={entry.id}
                    initialDurationMinutes={entry.durationMinutes}
                    initialNote={entry.note ?? null}
                    initialStartTime={entry.startTime}
                    onSaved={handleSaved}
                    onCancel={() => setIsEditing(false)}
                />
            </li>
        );
    }

    return (
        <li className="group flex items-baseline gap-2 text-xs text-zinc-700 dark:text-zinc-200">
            <span className="shrink-0 w-10 text-right font-semibold text-zinc-800 tabular-nums dark:text-zinc-100">
                {formatDuration(entry.durationMinutes)}
            </span>
            <span className="shrink-0 w-10 text-zinc-500 tabular-nums dark:text-zinc-400">
                {compactDate(entry.startTime)}
            </span>
            <span className="min-w-0 flex-1 truncate">
                {entry.note ?? (
                    <span className="italic text-zinc-400">No description</span>
                )}
            </span>
            {isDeleting ? (
                <span className="flex items-center gap-1 shrink-0 italic text-zinc-500">
                    Deleting…
                    <Spinner />
                </span>
            ) : (
                <span className="flex shrink-0 gap-0.5 text-gray-300 group-hover:text-gray-700">
                    <EditButton
                        size="sm"
                        backgroundColor="bg-gray-200"
                        onClick={() => setIsEditing(true)}
                    />
                    <DeleteButton
                        size="sm"
                        backgroundColor="bg-gray-200"
                        confirm={{
                            title: "Delete time entry?",
                            message: "Are you sure you want to delete this time entry?",
                        }}
                        onClick={handleDelete}
                    />
                </span>
            )}
        </li>
    );
};

function compactDate(isoStartTime: string): string {
    const calendarDate = CalendarDate.parse(isoStartTime).getOrElse(
        CalendarDate.todayNoTime,
    );
    return `${calendarDate.month}/${calendarDate.day}`;
}

const ReportLink: React.FC<{ onNavigate?: () => void }> = ({ onNavigate }) => {
    return (
        <Link
            to="/reports/show/time-tracking"
            onClick={onNavigate}
            className="inline-block text-xs font-medium text-brand-600 hover:text-brand-800">
            View time tracking report →
        </Link>
    );
};
